import { stripHtmlTags } from '@change-corgi/core/html';

import { getUsState } from 'src/app/shared/utils/unitedStatesMapping';

import type { AllDmResponses, BuildingBlockDms, DMs, NormalizedDecisionMaker } from '../../shared/types';
import { MAX_DISPLAYED_DMS_ON_LB_CARD, MAX_DISPLAYED_DMS_ON_PETITION, PRIMARY_ROLE_KEYWORDS } from '../../shared/types';

export function sortIndividualDms(dms: NormalizedDecisionMaker[]): NormalizedDecisionMaker[] {
	const preppedDms = dms.map((dm) => ({
		dm,
		hasResponse: !!dm.response,
		constituentCount: dm.constituentCount ? parseInt(dm.constituentCount.replace(/,/g, ''), 10) || 0 : 0,
		isAutoAdded: !!dm.isAutoAdded,
	}));

	preppedDms.sort((a, b) => {
		// First prioritize those with a response
		if (a.hasResponse !== b.hasResponse) return a.hasResponse ? -1 : 1;
		// Next prioritize highest constituentCounts
		if (a.constituentCount !== b.constituentCount) return b.constituentCount - a.constituentCount;
		// Next prioritize those auto-added by DMI
		if (a.isAutoAdded !== b.isAutoAdded) return a.isAutoAdded ? -1 : 1;

		return 0;
	});

	return preppedDms.map((x) => x.dm);
}

export function getConstituencyStat(constituentCount: string | null | undefined): string {
	if (!constituentCount) return '';
	return `Represents ${constituentCount} petition ${constituentCount === '1' ? 'signer' : 'signers'}`;
}

export const getLocation = (state: string | null, title: string | null): string | null => {
	if (!state) return null;

	const usKeywords = ['U.S.', 'US', 'United States', 'State'];
	const hasUsKeyword = usKeywords.some((keyword) => title?.includes(keyword));

	if (hasUsKeyword) return null;

	const fullStateName = getUsState(state);
	if (title?.includes(fullStateName)) return null;

	return fullStateName;
};

export const getViewMoreText = (decisionMakersCount: number) => {
	const remainingCount = decisionMakersCount - MAX_DISPLAYED_DMS_ON_LB_CARD;
	if (remainingCount <= 0) return undefined;

	switch (remainingCount) {
		case 1:
			return '+ 1 member';
		default:
			return `+ ${remainingCount} members`;
	}
};

export function getResponseText(sortedLBDMs: NormalizedDecisionMaker[]) {
	const responsesCount = sortedLBDMs.reduce((acc, nextVal) => {
		return nextVal.response ? acc + 1 : acc;
	}, 0);

	let responseText;
	switch (responsesCount) {
		case 0:
			responseText = undefined;
			break;
		case 1:
			responseText = '1 Response';
			break;
		default:
			responseText = `${responsesCount} Responses`;
			break;
	}
	return responseText;
}

export function normalizeDms(dms: DMs, allDmResponses: AllDmResponses): NormalizedDecisionMaker[] {
	return dms.map((dm) => {
		const dmResponse = allDmResponses.find((response) => response.decisionMaker.id === dm.id);
		const constituentCount = dm.targetPetitionAssociationData?.constituentCountAmongSignersLocalized;

		return {
			id: dm.id,
			displayName: dm.displayName,
			slug: dm.slug,
			title: dm.title ? dm.title.replace(/\bUS\b/g, 'U.S.') : null,
			titleStrippedDown: dm.title ? getTitleStrippedDown(dm.title) : null,
			isAutoAdded: dm.targetPetitionAssociationData?.isAutoAdded || false,
			location: getLocation(dm.state, dm.title),
			photoUrl: dm.photo?.userMedium.url ? dm.photo.userMedium.url : null,
			constituentCount,
			signersText: getConstituencyStat(constituentCount),
			publiclyVisible: dm.publiclyVisible,
			type: dm.type,
			...(dmResponse?.message ? { response: stripHtmlTags(dmResponse.message) } : {}),
		};
	});
}

export function getPrimaryDms(politicians: NormalizedDecisionMaker[]): NormalizedDecisionMaker[] {
	const primaries = politicians.filter((dm) => {
		if (!dm.title) return false;
		const title = dm.title.toLowerCase();
		return PRIMARY_ROLE_KEYWORDS.some((role) => title.includes(role));
	});
	return primaries;
}

export function getTitleStrippedDown(title: string): string {
	const titleBeforeHyphen = title.split('-')[0].trim();
	return titleBeforeHyphen.replace(/\bUS\b/g, 'U.S.');
}

export function getLegislativeBodies(
	dms: NormalizedDecisionMaker[],
): Array<{ groupTitle: string; groupDms: NormalizedDecisionMaker[] }> {
	const groups = new Map<string, NormalizedDecisionMaker[]>();
	for (const dm of dms) {
		if (dm.title) {
			const key = getTitleStrippedDown(dm.title);

			// 'Senator' is not a legislative body. Replace with 'Senate'.
			//  Strip 'Former' prefix in attempt to match any existing legislative bodies.
			const groupKey = key.includes('Senator') ? key.replace('Former ', '').replace('Senator', 'Senate') : key;

			if (!groups.has(groupKey)) {
				groups.set(groupKey, []);
			}
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			groups.get(groupKey)!.push(dm);
		}
	}

	return Array.from(groups.entries())
		.filter(([, items]) => items.length > 1)
		.map(([key, items]) => ({ groupTitle: key, groupDms: items }));
}

export function formatAllBuckets(
	primaryDms: NormalizedDecisionMaker[],
	legislativeBodies: Array<{ title: string; location: string | null; decisionMakers: NormalizedDecisionMaker[] }>,
	individualPoliticians: NormalizedDecisionMaker[],
	remainingDMs: NormalizedDecisionMaker[],
): BuildingBlockDms {
	const primaryDmsFormatted = primaryDms.map((dm) => ({ component: 'primary' as const, componentDm: dm }));
	const legislativeBodiesFormatted = legislativeBodies.map(({ decisionMakers, ...rest }) => ({
		component: 'legislativeBody' as const,
		componentDms: [...decisionMakers],
		componentAttributes: {
			...rest,
			displayedDMs: decisionMakers.slice(0, MAX_DISPLAYED_DMS_ON_LB_CARD),
			...(decisionMakers.length > MAX_DISPLAYED_DMS_ON_LB_CARD && {
				viewMoreText: getViewMoreText(decisionMakers.length),
			}),
		},
	}));
	const individualPoliticiansFormatted = individualPoliticians.map((dm) => ({
		component: 'individual' as const,
		componentDm: dm,
		componentAttributes: { type: 'politician' as const },
	}));
	const remainingDMsFormatted = remainingDMs.map((dm) => ({
		component: 'individual' as const,
		componentDm: dm,
		componentAttributes: { type: 'other' as const },
	}));

	return [
		...primaryDmsFormatted,
		...legislativeBodiesFormatted,
		...individualPoliticiansFormatted,
		...remainingDMsFormatted,
	];
}

export function sortAllDms(allFormattedBucketsOfDms: BuildingBlockDms): BuildingBlockDms {
	const grabTopDmFromEachBlock = (block: BuildingBlockDms[number]) => {
		if (block.component === 'primary' || block.component === 'individual') {
			return block.componentDm;
		}
		return block.componentDms[0];
	};

	const preppedDms = allFormattedBucketsOfDms.map((block) => {
		const dm = grabTopDmFromEachBlock(block);
		return {
			block,
			hasResponse: !!dm.response,
			constituentCount: dm.constituentCount ? parseInt(dm.constituentCount.replace(/,/g, ''), 10) || 0 : 0,
			isAutoAdded: !!dm.isAutoAdded,
		};
	});

	preppedDms.sort((a, b) => {
		// First prioritize those with a response
		if (a.hasResponse !== b.hasResponse) return a.hasResponse ? -1 : 1;
		// Next prioritize highest constituentCounts
		if (a.constituentCount !== b.constituentCount) return b.constituentCount - a.constituentCount;
		// Next prioritize those auto-added by DMI
		if (a.isAutoAdded !== b.isAutoAdded) return a.isAutoAdded ? -1 : 1;
		return 0;
	});

	// Make an exception to prioritization rule
	// Return one primary DM within the allowed display range if primary DMs exist
	// Return one legislative body within the allowed display range if lbs exist
	if (preppedDms.length > MAX_DISPLAYED_DMS_ON_PETITION) {
		if (
			preppedDms.some((x) => x.block.component === 'legislativeBody') &&
			!preppedDms.slice(0, MAX_DISPLAYED_DMS_ON_PETITION).some((x) => x.block.component === 'legislativeBody')
		) {
			const firstLBDmIndex = preppedDms.findIndex((x) => x.block.component === 'legislativeBody');
			const [firstLB] = preppedDms.splice(firstLBDmIndex, 1);
			preppedDms.splice(0, 0, firstLB);
		}
		if (
			preppedDms.some((x) => x.block.component === 'primary') &&
			!preppedDms.slice(0, MAX_DISPLAYED_DMS_ON_PETITION).some((x) => x.block.component === 'primary')
		) {
			const firstPrimaryDmIndex = preppedDms.findIndex((x) => x.block.component === 'primary');
			const [firstPrimary] = preppedDms.splice(firstPrimaryDmIndex, 1);
			preppedDms.splice(0, 0, firstPrimary);
		}
	}

	return preppedDms.map((x) => x.block);
}
